import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Fade,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as BotIcon,
  SkipNext as SkipIcon,
  CheckCircle as CheckIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  Videocam as VideoIcon,
  Call as CallIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  EmojiEmotions as EmojiIcon,
  MoreVert as MoreIcon,
  Contacts as ContactsIcon,
  AddComment as NewChatIcon,
  ArrowBack as BreadcrumbIcon,
} from "@mui/icons-material";
import { alpha, styled, keyframes } from "@mui/material/styles";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const pulseRing = keyframes`
  0%   { transform: scale(0.8); opacity: 0.8; }
  50%  { transform: scale(1.2); opacity: 0.4; }
  100% { transform: scale(0.8); opacity: 0.8; }
`;

const floatBot = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const typingDot = keyframes`
  0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
  40%            { transform: scale(1);   opacity: 1;   }
`;

const slideInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const BotAvatar = styled(Avatar)(() => ({
  width: 56,
  height: 56,
  background: "linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)",
  boxShadow: "0 4px 20px rgba(0,188,212,0.5)",
  animation: `${floatBot} 3s ease-in-out infinite`,
  cursor: "pointer",
  border: "3px solid white",
  transition: "transform 0.2s",
  "&:hover": { transform: "scale(1.1) translateY(-4px)" },
}));

const SpotlightOverlay = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 1200,
  pointerEvents: "none",
});

const BotCard = styled(Paper)(() => ({
  position: "fixed",
  zIndex: 1400,
  borderRadius: 20,
  overflow: "hidden",
  boxShadow: "0 24px 60px rgba(0,0,0,0.25), 0 8px 20px rgba(0,188,212,0.3)",
  minWidth: 320,
  maxWidth: 390,
  animation: `${slideInUp} 0.4s ease`,
  border: "1px solid rgba(255,255,255,0.2)",
  backdropFilter: "blur(12px)",
}));

const ProgressBar = styled(LinearProgress)(() => ({
  height: 4,
  borderRadius: 0,
  "& .MuiLinearProgress-bar": {
    background: "linear-gradient(90deg, #00BCD4, #0097A7)",
  },
  backgroundColor: "rgba(0,188,212,0.15)",
}));

const NextButton = styled(Button)(() => ({
  borderRadius: 50,
  textTransform: "none",
  fontWeight: 700,
  background: "linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)",
  color: "white",
  padding: "8px 24px",
  "&:hover": {
    background: "linear-gradient(135deg, #0097A7 0%, #00BCD4 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(0,188,212,0.4)",
  },
  transition: "all 0.2s",
}));

const PulseRing = styled(Box)(({ ringcolor = "#00BCD4" }) => ({
  position: "absolute",
  width: 60,
  height: 60,
  borderRadius: "50%",
  border: `3px solid ${ringcolor}`,
  animation: `${pulseRing} 1.5s ease-out infinite`,
  pointerEvents: "none",
}));

const TypingIndicator = () => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8, height: 8, borderRadius: "50%",
          background: "linear-gradient(135deg, #00BCD4, #0097A7)",
          animation: `${typingDot} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.16}s`,
        }}
      />
    ))}
  </Box>
);

// ─── Chat Tour Steps ──────────────────────────────────────────────────────────
const CHAT_TOUR_STEPS = [
  {
    id: "welcome",
    targetId: null,
    title: "💬 Welcome to Chat!",
    description:
      "Hi! I'm your Chat Guide Bot. Let me show you all the powerful messaging features in this Chat screen — from contacts to group chats and video calls!",
    color: "#00BCD4",
    position: "center",
    highlight: null,
  },
  {
    id: "breadcrumb",
    targetId: "chat-tour-breadcrumb",
    title: "🗺️ Navigation Breadcrumb",
    description:
      "Use the breadcrumb at the top to quickly navigate back to the Teams workspace. Click 'Teams' to go back and manage your team members and groups.",
    color: "#667eea",
    position: "bottom",
    highlight: "Quick navigation back to Teams",
  },
  {
    id: "contacts-panel",
    targetId: "chat-tour-contacts-panel",
    title: "📋 Contacts Sidebar",
    description:
      "The left sidebar shows all your contacts and active conversations. Direct chats and group chats are listed here. Click any contact to open the conversation.",
    color: "#4CAF50",
    position: "right",
    highlight: "All your conversations in one place",
  },
  {
    id: "contacts-search",
    targetId: "chat-tour-contacts-search",
    title: "🔍 Search Conversations",
    description:
      "Quickly find a specific contact or group chat by typing in the search bar. Results filter instantly as you type — no need to scroll through long lists!",
    color: "#FF9800",
    position: "right",
    highlight: "Find conversations instantly",
  },
  {
    id: "new-chat",
    targetId: "chat-tour-new-chat",
    title: "✉️ Start New Chat",
    description:
      "Click this button to start a brand new direct conversation with any of your team members. Just select a contact and you're ready to chat!",
    color: "#9C27B0",
    position: "right",
    highlight: "Start a new direct message",
  },
  {
    id: "create-group-chat",
    targetId: "chat-tour-create-group",
    title: "👥 Create Group Chat",
    description:
      "Create a new group conversation with multiple team members. Give the group a name, select members, and everyone can collaborate in one channel!",
    color: "#667eea",
    position: "right",
    highlight: "Multi-member group conversations",
  },
  {
    id: "contact-item",
    targetId: "chat-tour-contact-item",
    title: "🧑 Contact / Chat Item",
    description:
      "Each item shows the contact's name, avatar, last message preview, and timestamp. Unread messages appear with a bold count badge so you never miss anything!",
    color: "#2196F3",
    position: "right",
    highlight: "Tap any contact to open chat",
  },
  {
    id: "chat-area",
    targetId: "chat-tour-chat-area",
    title: "💬 Chat Area",
    description:
      "The main chat area displays your full conversation history. Messages are shown with timestamps and sender avatars. Scroll up to view older messages.",
    color: "#00BCD4",
    position: "left",
    highlight: "Your full conversation history",
  },
  {
    id: "chat-header",
    targetId: "chat-tour-chat-header",
    title: "🔝 Chat Header",
    description:
      "The header shows who you're chatting with, their online status (green dot = online), and quick action buttons for calls, video, and more options.",
    color: "#3F51B5",
    position: "bottom",
    highlight: "Contact info & quick actions",
  },
  {
    id: "video-call",
    targetId: "chat-tour-video-call",
    title: "📹 Video Call",
    description:
      "Start a one-on-one or group video call directly from the chat header. Perfect for face-to-face meetings without leaving the platform!",
    color: "#E91E63",
    position: "bottom",
    highlight: "Face-to-face video meetings",
  },
  {
    id: "voice-call",
    targetId: "chat-tour-voice-call",
    title: "📞 Voice Call",
    description:
      "Initiate a voice-only call with your team member. Great for quick verbal discussions when video isn't necessary. Hands-free communication!",
    color: "#4CAF50",
    position: "bottom",
    highlight: "Quick voice conversations",
  },
  {
    id: "message-input",
    targetId: "chat-tour-message-input",
    title: "✍️ Message Input",
    description:
      "Type your message here. Press Enter to send or use the Send button. The input supports long messages, and you can press Shift+Enter for a new line.",
    color: "#00BCD4",
    position: "top",
    highlight: "Type and send messages",
  },
  {
    id: "emoji",
    targetId: "chat-tour-emoji",
    title: "😊 Emoji Picker",
    description:
      "Add fun and expression to your messages with the emoji picker. Click the smiley face to browse hundreds of emojis and reactions!",
    color: "#FF9800",
    position: "top",
    highlight: "Express yourself with emojis",
  },
  {
    id: "attachment",
    targetId: "chat-tour-attachment",
    title: "📎 File Attachments",
    description:
      "Share files, documents, images, and more directly in the chat. Click the attachment icon to browse and upload files from your device.",
    color: "#607D8B",
    position: "top",
    highlight: "Share files in chat",
  },
  {
    id: "send-btn",
    targetId: "chat-tour-send-btn",
    title: "🚀 Send Message",
    description:
      "Click the Send button (or press Enter) to deliver your message instantly. Messages are delivered in real-time to all participants in the conversation!",
    color: "#4CAF50",
    position: "top",
    highlight: "Send messages in real-time",
  },
  {
    id: "done",
    targetId: null,
    title: "🎉 Chat Tour Complete!",
    description:
      "You've explored all the chat features! You can now send messages, make calls, share files, and collaborate with your team in real-time. Happy chatting!",
    color: "#4CAF50",
    position: "center",
    highlight: null,
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ChatFeatureBot({
  show = true,
  autoStart = false,
  onAutoStart,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [targetRect, setTargetRect] = useState(null);
  const [cardPos, setCardPos] = useState({ top: "50%", left: "50%", transform: "translate(-50%,-50%)" });
  const [isVisible, setIsVisible] = useState(false);
  const typingRef = useRef(null);

  const step = CHAT_TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / CHAT_TOUR_STEPS.length) * 100;

  const findTarget = useCallback((targetId) => {
    if (!targetId) return null;
    return document.getElementById(targetId);
  }, []);

  const positionCard = useCallback((el, position) => {
    if (!el) {
      setTargetRect(null);
      setCardPos({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });
      return;
    }

    el.scrollIntoView({ behavior: "smooth", block: "center" });

    setTimeout(() => {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      const cardW = 390;
      const cardH = 280;
      const margin = 16;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let top, left;

      switch (position) {
        case "bottom": top = rect.bottom + margin; left = rect.left + rect.width / 2 - cardW / 2; break;
        case "top":    top = rect.top - cardH - margin; left = rect.left + rect.width / 2 - cardW / 2; break;
        case "right":  top = rect.top + rect.height / 2 - cardH / 2; left = rect.right + margin; break;
        case "left":   top = rect.top + rect.height / 2 - cardH / 2; left = rect.left - cardW - margin; break;
        default:       top = rect.bottom + margin; left = rect.left + rect.width / 2 - cardW / 2;
      }

      left = Math.max(margin, Math.min(left, vw - cardW - margin));
      top = Math.max(margin, Math.min(top, vh - cardH - margin));

      setCardPos({ top, left, transform: "none" });
    }, 400);
  }, []);

  const typeText = useCallback((text) => {
    setIsTyping(true);
    setDisplayedText("");
    let i = 0;
    clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(typingRef.current);
        setIsTyping(false);
      }
    }, 16);
  }, []);

  const goToStep = useCallback((idx) => {
    const s = CHAT_TOUR_STEPS[idx];
    setCurrentStep(idx);
    const el = findTarget(s.targetId);
    positionCard(el, s.position);
    setIsVisible(false);
    setTimeout(() => {
      setIsVisible(true);
      typeText(s.description);
    }, 300);
  }, [findTarget, positionCard, typeText]);

  const startTour = () => { setIsOpen(true); goToStep(0); };

  const closeTour = () => {
    setIsOpen(false);
    setCurrentStep(0);
    setTargetRect(null);
    clearInterval(typingRef.current);
  };

  const next = () => { if (currentStep < CHAT_TOUR_STEPS.length - 1) goToStep(currentStep + 1); else closeTour(); };
  const prev = () => { if (currentStep > 0) goToStep(currentStep - 1); };

  useEffect(() => () => clearInterval(typingRef.current), []);

  useEffect(() => {
    if (show && autoStart && !isOpen) {
      startTour();
      if (typeof onAutoStart === "function") {
        onAutoStart();
      }
    }
  }, [show, autoStart, isOpen, onAutoStart]);

  if (!show) return null;

  return (
    <>
      {/* ── Floating Launcher ── */}
      {!isOpen && (
        <Box sx={{ position: "fixed", bottom:95, right: 32, zIndex: 1300, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Fade in>
            <Box sx={{ position: "relative" }}>
              <PulseRing ringcolor="#00BCD4" sx={{ top: "-2px", left: "-2px", width: 60, height: 60 }} />
              <BotAvatar onClick={startTour}>
                <BotIcon sx={{ fontSize: 28 }} />
              </BotAvatar>
            </Box>
          </Fade>
          <Paper
            elevation={4}
            sx={{ px: 2, py: 0.75, borderRadius: 50, background: "linear-gradient(135deg, #00BCD4, #0097A7)", cursor: "pointer", backgroundSize: "200% auto", animation: `${shimmer} 3s linear infinite` }}
            onClick={startTour}
          >
            <Typography variant="caption" fontWeight={700} color="white" noWrap>💬 Chat Tour</Typography>
          </Paper>
        </Box>
      )}

      {/* ── Spotlight Overlay ── */}
      {isOpen && (
        <SpotlightOverlay>
          <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
            <defs>
              <mask id="chat-spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                {targetRect && (
                  <rect x={targetRect.left - 8} y={targetRect.top - 8} width={targetRect.width + 16} height={targetRect.height + 16} rx={12} fill="black" />
                )}
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#chat-spotlight-mask)" />
            {targetRect && (
              <>
                <rect x={targetRect.left - 8} y={targetRect.top - 8} width={targetRect.width + 16} height={targetRect.height + 16} rx={12} fill="none" stroke={step.color} strokeWidth={3} opacity={0.9} />
                {[
                  [targetRect.left - 10, targetRect.top - 10],
                  [targetRect.right - 20, targetRect.top - 10],
                  [targetRect.left - 10, targetRect.bottom - 20],
                  [targetRect.right - 20, targetRect.bottom - 20],
                ].map(([cx, cy], i) => (
                  <g key={i} transform={`translate(${cx},${cy})`}>
                    <rect width={20} height={20} rx={4} fill={step.color} opacity={0.25} />
                  </g>
                ))}
              </>
            )}
          </svg>
        </SpotlightOverlay>
      )}

      {/* ── Bot Card ── */}
      {isOpen && (
        <BotCard style={{ top: cardPos.top, left: cardPos.left, transform: cardPos.transform }}>
          <ProgressBar variant="determinate" value={progress} />

          {/* Header */}
          <Box sx={{ background: `linear-gradient(135deg, ${step.color}dd, ${step.color}99)`, px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ width: 38, height: 38, background: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.4)", animation: `${floatBot} 3s ease-in-out infinite` }}>
              <BotIcon sx={{ fontSize: 20, color: "white" }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", fontSize: 10 }}>
                Chat Guide Bot
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} color="white" lineHeight={1.2}>
                {step.title}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip label={`${currentStep + 1}/${CHAT_TOUR_STEPS.length}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 700, fontSize: 10, height: 22 }} />
              <IconButton size="small" onClick={closeTour} sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,68,68,0.6)" }, width: 28, height: 28 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ px: 2.5, py: 2, bgcolor: "white", minHeight: 90 }}>
            {step.highlight && (
              <Chip
                label={step.highlight}
                size="small"
                sx={{ mb: 1.5, bgcolor: alpha(step.color, 0.1), color: step.color, fontWeight: 600, fontSize: 11, border: `1px solid ${alpha(step.color, 0.3)}` }}
              />
            )}
            <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
              {displayedText}
              {isTyping && (
                <Box component="span" sx={{ display: "inline-block", ml: 0.5 }}>
                  <TypingIndicator />
                </Box>
              )}
            </Typography>
          </Box>

          {/* Footer */}
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#f0fdff", borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              {currentStep > 0 && (
                <IconButton size="small" onClick={prev} sx={{ border: "1px solid rgba(0,188,212,0.3)", color: "#00BCD4", "&:hover": { bgcolor: alpha("#00BCD4", 0.08) }, width: 32, height: 32 }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              <Button size="small" startIcon={<SkipIcon fontSize="small" />} onClick={closeTour} sx={{ textTransform: "none", color: "text.secondary", fontSize: 12, "&:hover": { color: "error.main" } }}>
                Skip tour
              </Button>
            </Box>
            <NextButton size="small" onClick={next} endIcon={<ArrowForwardIcon />}>
              {currentStep === CHAT_TOUR_STEPS.length - 1 ? "Finish 🎉" : "Next"}
            </NextButton>
          </Box>
        </BotCard>
      )}
    </>
  );
}
